/**
 * Unified IAP Service
 * 
 * This service provides a unified interface for in-app purchases
 * across all platforms (iOS, Android, Web). It delegates to the
 * appropriate platform-specific implementation.
 */

import { Capacitor } from '@capacitor/core';
import { 
  IAPService, 
  IAPOffering, 
  IAPCustomerInfo, 
  IAPPurchaseResult, 
  IAPRestoreResult,
  IAPSubscriptionStatus
} from './types';
import { isIAPEnabled, shouldUseFirebaseFallback, shouldHonorLegacySubscriptions, REVENUECAT_CONFIG } from './config';
import { revenueCatNativeService } from './revenuecat-native.service';
import { revenueCatWebService } from './revenuecat-web.service';
import { updateSubStatus } from '../firebase/profile.service';
import { store } from '../../store';
import logger from '../../utils/logger';

class UnifiedIAPService implements IAPService {
  private platformService: IAPService | null = null;
  private currentUserId: string | null = null;

  constructor() {
    this.detectPlatform();
  }

  private detectPlatform() {
    if (Capacitor.isNativePlatform()) {
      this.platformService = revenueCatNativeService;
    } else {
      this.platformService = revenueCatWebService;
    }
  }

  async initialize(userId: string): Promise<void> {
    try {
      this.currentUserId = userId;

      if (!isIAPEnabled()) {
        logger.info('IAP is disabled via feature flag');
        return;
      }

      if (!this.platformService) {
        logger.warn('No platform service available');
        return;
      }

      await this.platformService.initialize(userId);
      
      // Perform initial subscription check
      await this.syncSubscriptionStatus();
    } catch (error) {
      logger.error('Failed to initialize unified IAP service', error);
      
      // Don't throw - we don't want to break app startup
      if (shouldUseFirebaseFallback()) {
        logger.info('Using Firebase fallback for subscription status');
      }
    }
  }

  async getOfferings(): Promise<IAPOffering[]> {
    if (!this.checkAvailability()) return [];
    
    try {
      return await this.platformService!.getOfferings();
    } catch (error) {
      logger.error('Failed to get offerings', error);
      return [];
    }
  }

  async getCustomerInfo(): Promise<IAPCustomerInfo> {
    if (!this.checkAvailability()) {
      throw new Error('IAP not available');
    }
    
    return await this.platformService!.getCustomerInfo();
  }

  async purchasePackage(packageId: string): Promise<IAPPurchaseResult> {
    if (!this.checkAvailability()) {
      return {
        success: false,
        error: {
          code: 'IAP_NOT_AVAILABLE',
          message: 'In-app purchases are not available'
        }
      };
    }
    
    try {
      const result = await this.platformService!.purchasePackage(packageId);
      
      if (result.success) {
        // Sync subscription status after successful purchase
        await this.syncSubscriptionStatus();
      }
      
      return result;
    } catch (error) {
      logger.error('Purchase failed', error);
      return {
        success: false,
        error: {
          code: 'PURCHASE_FAILED',
          message: 'Purchase failed. Please try again.'
        }
      };
    }
  }

  async restorePurchases(): Promise<IAPRestoreResult> {
    if (!this.checkAvailability()) {
      return {
        success: false,
        error: {
          code: 'IAP_NOT_AVAILABLE',
          message: 'In-app purchases are not available'
        }
      };
    }
    
    try {
      const result = await this.platformService!.restorePurchases();
      
      if (result.success) {
        // Sync subscription status after successful restore
        await this.syncSubscriptionStatus();
      }
      
      return result;
    } catch (error) {
      logger.error('Restore failed', error);
      return {
        success: false,
        error: {
          code: 'RESTORE_FAILED',
          message: 'Restore failed. Please try again.'
        }
      };
    }
  }

  async getSubscriptionStatus(): Promise<IAPSubscriptionStatus> {
    // If IAP is disabled or in Firebase-only mode, check Firebase only
    if (!isIAPEnabled() || shouldUseFirebaseFallback()) {
      return this.getFirebaseSubscriptionStatus();
    }

    if (!this.checkAvailability()) {
      return this.getFirebaseSubscriptionStatus();
    }

    try {
      // Get status from RevenueCat (source of truth for active subscriptions)
      const rcStatus = await this.platformService!.getSubscriptionStatus();

      // If RevenueCat shows active, use that
      if (rcStatus.isPeakSubscriber) {
        return rcStatus;
      }

      // If RevenueCat shows inactive but we honor legacy subscriptions,
      // check Firebase as well (for users who subscribed before RevenueCat integration)
      if (shouldHonorLegacySubscriptions()) {
        const fbStatus = await this.getFirebaseSubscriptionStatus();
        if (fbStatus.isPeakSubscriber) {
          logger.info('User has legacy Firebase subscription, honoring it');
          return fbStatus;
        }
      }

      // RevenueCat says no subscription, return that
      return rcStatus;
    } catch (error) {
      logger.error('Failed to get subscription status from RevenueCat', error);

      // Fallback to Firebase on error
      return this.getFirebaseSubscriptionStatus();
    }
  }

  async syncSubscriptionStatus(): Promise<void> {
    try {
      if (!this.currentUserId) {
        logger.warn('No user ID for subscription sync');
        return;
      }

      // Get status from RevenueCat
      const status = await this.getSubscriptionStatus();
      
      // Get current Firebase status
      const state = store.getState();
      const currentProfile = state.user.currentProfile;
      
      if (!currentProfile) {
        logger.warn('No user profile for subscription sync');
        return;
      }

      const firebaseStatus = currentProfile.isPeakSub || false;
      const revenueCatStatus = status.isPeakSubscriber;

      // Update Firebase if status changed
      if (firebaseStatus !== revenueCatStatus) {
        logger.info('Subscription status changed', {
          old: firebaseStatus,
          new: revenueCatStatus
        });
        
        await updateSubStatus(revenueCatStatus);
        
        // Dispatch action to update Redux store
        store.dispatch({
          type: 'user/updateProfile',
          payload: {
            isPeakSub: revenueCatStatus
          }
        });
      }
    } catch (error) {
      logger.error('Failed to sync subscription status', error);
    }
  }

  async logOut(): Promise<void> {
    if (this.platformService && this.isAvailable()) {
      await this.platformService.logOut();
    }
    this.currentUserId = null;
  }

  isAvailable(): boolean {
    return this.platformService?.isAvailable() || false;
  }

  // Helper methods

  private checkAvailability(): boolean {
    if (!isIAPEnabled()) {
      logger.warn('IAP is disabled');
      return false;
    }
    
    if (!this.platformService || !this.platformService.isAvailable()) {
      logger.warn('IAP service not available');
      return false;
    }
    
    return true;
  }

  private async getFirebaseSubscriptionStatus(): Promise<IAPSubscriptionStatus> {
    const state = store.getState();
    const currentProfile = state.user.currentProfile;
    
    const isPeakSub = currentProfile?.isPeakSub || false;
    
    return {
      isPeakSubscriber: isPeakSub,
      isAdFree: isPeakSub, // Peak includes ad-free
      activeSubscriptions: isPeakSub ? ['firebase_peak'] : [],
      entitlements: {
        all: {},
        active: isPeakSub ? {
          [REVENUECAT_CONFIG.entitlements.peak]: {
            identifier: REVENUECAT_CONFIG.entitlements.peak,
            isActive: true,
            willRenew: false, // Unknown from Firebase
            productIdentifier: 'unknown'
          }
        } : {}
      }
    };
  }
}

// Export singleton instance
export const unifiedIAPService = new UnifiedIAPService();

// Export convenience functions
export const initializeIAP = (userId: string) => unifiedIAPService.initialize(userId);
export const getOfferings = () => unifiedIAPService.getOfferings();
export const purchasePackage = (packageId: string) => unifiedIAPService.purchasePackage(packageId);
export const restorePurchases = () => unifiedIAPService.restorePurchases();
export const getSubscriptionStatus = () => unifiedIAPService.getSubscriptionStatus();
export const syncSubscriptionStatus = () => unifiedIAPService.syncSubscriptionStatus();